import { useSearchParams } from 'react-router-dom';
import { Card, CardContent} from '@/components/ui/card';
import { formatCurrency } from '@/lib/formatters';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';

function ThanksPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const resultParam = searchParams.get('result');
  
  let result = null;
  try {
    if (resultParam) {
      result = JSON.parse(decodeURIComponent(resultParam));
    }
  } catch (e) {
    console.error('Failed to parse result:', e);
  }

  return (
    <div className="max-w-3xl mx-auto">
      <Card className="border-[#ccd77b]">
       
        <CardContent className="pt-6">
          <div className="space-y-6">
          

            {result && (
              <div className="mt-8 space-y-4">
                <h3 className="text-xl font-semibold text-[#006b69]">
                  {t('thanks.calculationSummary', 'Your Calculation Summary')}
                </h3>
                
                <div className="bg-[#f7f8eb] p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg">{t('thanks.eligibilityStatus', 'Eligibility Status')}</span>
                    <div className="flex items-center gap-2">
                      {result.isEligible ? (
                        <>
                          <CheckCircle2 className="h-6 w-6 text-[#006b69]" />
                          <span className="font-bold text-[#006b69]">
                            {t('results.eligible', 'Eligible')}
                          </span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-6 w-6 text-[#e56581]" />
                          <span className="font-bold text-[#e56581]">
                            {t('results.notEligible', 'Not Eligible')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{t('results.netMonthlyIncome', 'Net Monthly Income')}</span>
                      <span className="text-lg font-semibold">
                        € {formatCurrency(result.paymentType === '14x' 
                          ? (result.netSalary * 14) / 12 
                          : result.netSalary)}
                      </span>
                    </div>

                    {result.paymentType === '14x' && (
                      <div className="flex justify-between text-sm text-muted-foreground pl-4">
                        <span>{t('results.originalMonthlySalary', 'Original Monthly Salary')}</span>
                        <span>€ {formatCurrency(result.netSalary)}</span>
                      </div>
                    )}

                    <div className="flex justify-between items-center">
                      <span className="font-medium">{t('results.otherMonthlyIncome', 'Other Monthly Income')}</span>
                      <span className="text-lg font-semibold">€ {formatCurrency(result.otherIncome)}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="font-medium">{t('results.monthlySavingsContribution', 'Monthly Savings Contribution')}</span>
                      <span className="text-lg font-semibold">€ {formatCurrency(result.savings / 12)}</span>
                    </div>

                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>{t('results.majorMonthlyExpenses', 'Major Monthly Expenses')}</span>
                      <span>€ {formatCurrency(result.majorExpenses)}</span>
                    </div>

                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>{t('results.expenseThreshold', 'Expense Threshold')}</span>
                      <span>€ {formatCurrency(result.expenseDeduction)}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="font-medium">{t('results.expensesExceedingThreshold', 'Expenses Exceeding Threshold')}:</span>
                      <span className="text-lg font-semibold text-[#e56581]">
                        - € {formatCurrency(Math.max(0, result.majorExpenses - result.expenseDeduction))}
                      </span>
                    </div>

                    <div className="border-t border-[#ccd77b] my-2" />

                    <div className="flex justify-between font-semibold text-xl bg-white p-4 rounded-md shadow-sm border border-[#ccd77b]">
                      <span>{t('results.totalEligibleIncome', 'Total Eligible Income')}</span>
                      <span>€ {formatCurrency(result.eligibleIncome)}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="font-medium">{t('results.requiredMinimumIncome', 'Required Minimum Income')}</span>
                      <span className="text-lg font-semibold">
                        € {formatCurrency(result.requiredIncome)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-[#ccd77b]">
                    <ul className="space-y-1 text-sm">
                  
                      {result.childrenCount > 0 && (
                        <li>
                          {t('thanks.children', 'Children')}  {' '}
                          <span className="font-medium">{result.childrenCount}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

            
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <div className="max-w-3xl md:max-w-fit mx-auto mt-8 p-4 bg-[#f5f7e5] rounded-lg flex justify-center">
        <a href="https://cta-eu1.hubspot.com/web-interactives/public/v1/track/redirect?encryptedPayload=AVxigLLQp7xCJc%2BNPkxU62hUiyUC%2FYMrvESSycR6Y7y0TW11LbGWcdqBRsXSJ%2FimkipVnVkh%2Bs0SxdtBbgyXKC0%2Ft6XO2NyHcfiMflMj4No6i8fmBKn7dAe71LuZyp5CQZYIx04IKICiwKHnhbTigD7YZWjPn05x5tE7%2FypFFHtZQCTZMNwplA%3D%3D&webInteractiveContentId=234541574356&portalId=139791174"
           target="_blank" rel="noopener">
          <img
              alt={t('footer.ctaAlt')}
              loading="lazy"
              src="https://hubspot-no-cache-eu1-prod.s3.amazonaws.com/cta/default/139791174/interactive-234541574356.png"
              className="border-b border-black object-contain"
          />
        </a>
      </div>
    </div>
  );
}

export default ThanksPage; 